import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { lessonTypeaheadSearch, useLessonById } from '@/lib/api-call-hooks.js';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { classTimeTypesFilterOption } from '@/lib/form-utils.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions.js';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions.js';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table.js';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { AdminSession, classTimeTypeSchema } from '@airreading/common/data';
import { pluralize } from '@/lib/string-utils.js';
import { studentTabs } from '../../-components/tabs.js';
import { validateRouteRole } from '@/lib/routing.js';

export const Route = createFileRoute('/_layout/students/$studentId/future-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentFutureSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    lessons: z.string().array().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    endDate: z.string().optional(),
  }),
});

function StudentFutureSessionsIndex() {
  const queryClient = useQueryClient();
  const { studentId } = Route.useParams();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'lessons', 'classTypes', 'endDate']);

  const startOfTomorrowInstantInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral)
    .add({ days: 1 })
    .startOfDay()
    .toInstant();
  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: startOfTomorrowInstantInCST.toString(),
        startOnOrBefore: endPlanDate
          ? endPlanDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        query: activeQuery,
        lessons: activeSelectedLessons,
        classTypes: activeSelectedClassTypes,
        studentIds: [studentId], // Filter by the current student ID
      },
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student, email, or teacher"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Lesson Name"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedLessons ?? []}
          onChange={setSelectedLessons}
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Class Type"
          options={classTimeTypesFilterOption}
          selectedValues={selectedClassTypes ?? []}
          onChange={setSelectedClassTypes}
        />
        <TableFilterPlainDatePicker
          selectedPlainDateString={selectedEndDate}
          onPlainDateSelect={setSelectedEndDate}
          tableFilterDatePickerProps={{
            placeholder: 'End date (CT)',
          }}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      fetchLessonById,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedEndDate,
    ],
  );

  return (
    <InfoPage
      headerText="Future Sessions"
      tabs={studentTabs({ studentId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Students',
            link: {
              to: '/students',
            },
          },
        ],
        currentPage: 'Future Sessions',
      }}
    >
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="future"
      />
    </InfoPage>
  );
}
